import Backbone from 'backbone';
import {queue} from 'd3-queue';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import notifications from 'notifications';
import DragDropUpload from 'util/DragDropUpload';

import FilesPickerItemTemplate, {FilesPickerItemProps, UploadStatus} from './FilesPickerItemTemplate';
import FilesPickerTemplate, {FilesPickerProps} from './FilesPickerTemplate';

interface UploadFileModelAttributes {
  file: File | null;
  name: string;
  status: UploadStatus;
  deleted: boolean;
  upload?: (file: File, name: string) => JQueryPromise<unknown>;
}

class UploadFileModel extends Backbone.Model {
  override defaults(): UploadFileModelAttributes {
    return {
      file: null,
      name: 'Untitled',
      status: UploadStatus.PENDING,
      deleted: false,
    };
  }

  // Resolves to `unknown` because callers disagree on what the resolved value represents (e.g.
  // FilesPickerComponent types its `upload` prop as resolving `void`, while NewEntryProtocolTab's
  // handler resolves an ExternalFile), and this file never inspects the resolved value itself.
  upload(): JQueryPromise<unknown> {
    this.set({status: UploadStatus.UPLOADING});
    return this.get('upload')(this.get('file'), this.get('name'));
  }
}

class UploadFileView extends Backbone.View<UploadFileModel> {
  static initClass(): void {
    this.prototype.template = FilesPickerItemTemplate;
    this.prototype.tagName = 'tr';
  }

  template!: (args: FilesPickerItemProps) => string;

  // Backbone's TS types only allow the events hash to be returned from an `events()` method, not
  // assigned as a plain object literal (the more common Backbone convention).
  override events(): Backbone.EventsHash {
    return {
      'click .delete': 'handleDelete',
    };
  }

  override initialize(): void {
    this.listenTo(this.model, 'change:deleted', () => {
      if (this.shouldRemove()) {
        this.remove();
      }
    });
    this.listenTo(this.model, 'change:name change:status', () => this.render());
  }

  override render(): this {
    this.$el.html(
      this.template({
        name: this.model.get('name'),
        status: this.model.get('status'),
      })
    );
    return this;
  }

  handleDelete(): void {
    this.model.set('deleted', true);
  }

  shouldRemove(): boolean {
    return this.model.get('status') === UploadStatus.PENDING && this.model.get('deleted');
  }
}
UploadFileView.initClass();

export interface FilesPickerOptions extends Backbone.ViewOptions<Backbone.Model> {
  isDisabled?: boolean;
  getName?: (file: File) => string;
  multiple?: boolean;
  // If bulkUpload is true, all the files will be sent as a list to the upload function and the
  // component expects a single promise indicating whether the bulk upload succeeded. Otherwise,
  // upload is called once per file.
  bulkUpload?: boolean;
  parallelism?: number;
  upload: (files: Array<File>) => JQueryPromise<unknown>;
  maxFiles?: number;
  fileTypes?: Array<string>;
  collection?: Backbone.Collection<UploadFileModel>;
}

type FilesPickerOptionsWithDefaults = FilesPickerOptions &
  Required<Pick<FilesPickerOptions, 'getName' | 'multiple' | 'parallelism' | 'bulkUpload'>>;

interface HandleUploadCallbacks {
  uploadComplete?: (response?: unknown, fileName?: string) => void;
  allComplete?: () => void;
}

class FilesPicker extends Backbone.View<Backbone.Model> {
  static initClass(): void {
    this.prototype.tagName = 'div';
    this.prototype.className = 'files-picker';
    this.prototype.template = FilesPickerTemplate;
  }

  template!: (args: FilesPickerProps) => string;
  isDisabled?: boolean;
  getName!: (file: File) => string;
  multiple!: boolean;
  bulkUpload!: boolean;
  parallelism!: number;
  upload!: (files: Array<File>) => JQueryPromise<unknown>;
  maxFiles?: number;
  fileTypes?: Array<string>;
  override collection!: Backbone.Collection<UploadFileModel>;

  constructor(options: FilesPickerOptions) {
    super(options);
  }

  override events(): Backbone.EventsHash {
    return {
      'click .file-choose': 'chooseFiles',
      'change .file': 'addFromChooser',
      filedrop: 'handleDrop',
    };
  }

  override initialize(options: FilesPickerOptions): void {
    this.handleUpload = this.handleUpload.bind(this);
    const optionsWithDefaults: FilesPickerOptionsWithDefaults = {
      getName: (file: File): string => file.name,
      multiple: true,
      parallelism: 2,
      bulkUpload: false,
      ...options,
    };
    super.initialize(optionsWithDefaults);
    if (this.collection == null) {
      this.collection = new Backbone.Collection([], {model: UploadFileModel});
    }

    this.listenTo(this.collection, 'change:deleted', (model: UploadFileModel, deleted: boolean) => {
      assert(deleted, 'Expected model to be marked as deleted when change:deleted fires');
      this.collection.remove(model);
      this.$('.js-files-count').text(this.collection.length);
    });

    this.isDisabled = optionsWithDefaults.isDisabled;
    this.getName = optionsWithDefaults.getName;
    this.multiple = optionsWithDefaults.multiple;
    this.bulkUpload = optionsWithDefaults.bulkUpload;
    this.parallelism = optionsWithDefaults.parallelism;
    this.upload = optionsWithDefaults.upload;
    this.maxFiles = optionsWithDefaults.maxFiles;
    this.fileTypes = optionsWithDefaults.fileTypes;
  }

  handleDrop(e: JQueryEventObject, files?: FileList): void {
    if (!this.isDisabled && files != null && files.length > 0) {
      this.addFiles(files);
    }
  }

  chooseFiles(): JQuery {
    return this.$('.file').click();
  }

  addFromChooser(e: JQueryEventObject): void {
    this.addFiles((e.target as HTMLInputElement).files ?? []);
  }

  addFiles(files: FileList | Array<File>): void {
    const fileArray = Array.from(files);
    const remaining = this.maxFiles != null ? this.maxFiles - this.collection.length : null;
    if (remaining != null && fileArray.length > remaining) {
      let msg: React.ReactElement = (
        <>
          You've exceeded the limit of <strong>{this.maxFiles} files</strong>.
        </>
      );
      if (remaining > 0) {
        msg = (
          <>
            {msg} Please try your action again but add{' '}
            <strong>no more than {remaining} additional files</strong>.
          </>
        );
      }
      notifications.warn(msg);
      return;
    }
    for (const file of fileArray) {
      const name = this.getName(file);
      const uploadModel = new UploadFileModel({file, name, upload: this.upload});
      this.registerUpload(uploadModel);
    }
  }

  registerUpload(uploadModel: UploadFileModel): void {
    this.$('.files-list').append(new UploadFileView({model: uploadModel}).render().el);
    this.$('.js-files-title').removeClass('hidden').attr('aria-hidden', 'false');
    if (!this.multiple) {
      // If single file upload, then remove the other existing files.
      this.collection.each((model) => {
        model.set('deleted', true);
      });
    }
    this.collection.add(uploadModel);
    this.$('.js-files-count').text(this.collection.length);
  }

  handleUpload({uploadComplete, allComplete}: HandleUploadCallbacks): JQueryPromise<unknown> | undefined {
    DragDropUpload.pop(this.el);
    this.$('.file-choose').addClass('disabled');

    if (this.bulkUpload) {
      return this.handleBulkUpload({allComplete});
    }
    this.handleSerialUpload({uploadComplete, allComplete});
    return undefined;
  }

  handleSerialUpload({uploadComplete, allComplete}: HandleUploadCallbacks): void {
    assert(
      _.isFunction(uploadComplete) && _.isFunction(allComplete),
      'uploadComplete and allComplete must be functions'
    );
    const uploadQueue = queue(this.parallelism);

    function task(uploadModel: UploadFileModel, callback: () => void): void {
      if (uploadModel.get('deleted')) {
        callback();
        return;
      }
      uploadModel
        .upload()
        .done(() => uploadModel.set('status', UploadStatus.COMPLETE))
        .fail(() => uploadModel.set('status', UploadStatus.FAILED))
        // `assert` above guarantees uploadComplete/allComplete are functions.
        .done((response: unknown) => {
          uploadComplete!(response, uploadModel.get('file').name);
        })
        .always(() => {
          callback();
        });
    }

    this.collection.each((model) => {
      uploadQueue.defer(task, model);
    });
    uploadQueue.awaitAll(() => {
      allComplete();
    });
  }

  setAllFilesStatus(status: UploadStatus): void {
    this.collection.each((model) => model.set({status}));
  }

  handleBulkUpload({allComplete}: Pick<HandleUploadCallbacks, 'allComplete'>): JQueryPromise<unknown> {
    assert(_.isFunction(allComplete), 'allComplete must be a function');
    this.setAllFilesStatus(UploadStatus.UPLOADING);
    const uploadPromise = this.upload(this.collection.map((model) => model.get('file')));
    // If this fails, make sure your `upload` prop returns a JQuery promise
    assert(uploadPromise != null, 'Upload handler should return a promise');
    return uploadPromise
      .fail(() => {
        this.setAllFilesStatus(UploadStatus.FAILED);
      })
      .done(() => {
        this.setAllFilesStatus(UploadStatus.COMPLETE);
      })
      .done(() => {
        allComplete();
      });
  }

  override remove(): Backbone.View<Backbone.Model> {
    DragDropUpload.pop(this.el);
    return super.remove();
  }

  override render(): this {
    this.$el.html(
      this.template({isDisabled: this.isDisabled, multiple: this.multiple, fileTypes: this.fileTypes})
    );
    DragDropUpload.push(this.el);
    return this;
  }
}
FilesPicker.initClass();

export default FilesPicker;
